<template>
  <div>
    <NavigationMobileTop v-if="isMessagesRoute" :links="messagesMobileLinks" />
    <div :class="{ 'max-lg:hidden': isMessagesRoute }">
      <header class="global-navigation flex justify-between">
        <NavigationLogo :to="preventRedirectInRegistration ? '' : '/account/seekers/dashboard'" />
        <router-link
          v-if="!basicAccount && !isSimpleHeader"
          to="/messages"
          class="flex mobile-messaging-icon mr-16"
          tabindex="0"
          :aria-current="activeIcon === 'messages' && 'page'"
          aria-label="Messages"
          role="link"
        >
          <messages-icon-outline />
          <badge v-if="unreadMessageCount > 0" :badge-content="unreadMessageCount" />
        </router-link>
        <div v-else-if="isSimpleHeader" class="flex h-full shrink-0 flex-row items-center justify-center mr-16 md-1:hidden">
          <GlobalNavDropdown
            id="mobile-account-menu-btn"
            :name="account.name"
            :first-name="account.firstName"
            :last-name="account.lastName"
            :profile-image="avatarUrl"
            :account-type="accountType"
            :is-simple-header="isSimpleHeader"
            overlay-position="top"
            size="small"
            trigger-layout="row"
          />
        </div>
        <Button
          v-else
          class="mr-16 md-1:hidden"
          value="Upgrade"
          variant="primary"
          href="/upgrade/pricing"
          size="small"
          @click="upgradeClicked"
        />
        <nav class="flex h-full items-center mr-24 icon-container hidden" aria-label="Primary Navigation">
          <NavigationItem v-if="!isSimpleHeader" text="Home" to="/account/seekers/dashboard" :is-active="activeIcon === 'home'">
            <home-icon-filled v-if="activeIcon === 'home'" />
            <home-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!isSimpleHeader"
            text="Jobs"
            to="/seeker/jobs"
            :badge-num="receivedAppsCount"
            :is-active="activeIcon === 'jobs'"
          >
            <jobs-icon-filled v-if="activeIcon === 'jobs'" />
            <jobs-icon-outline v-else />
          </NavigationItem>
          <NavigationItem v-if="!isSimpleHeader" text="Explore" to="/availability/search" :is-active="activeIcon === 'explore'">
            <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
            <magnifying-glass-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!basicAccount && !isSimpleHeader"
            text="Messages"
            to="/messages"
            :badge-num="unreadMessageCount"
            :is-active="activeIcon === 'messages'"
          >
            <messages-icon-filled v-if="activeIcon === 'messages'" />
            <messages-icon-outline v-else />
          </NavigationItem>
          <NavigationItem
            v-if="!basicAccount && !isSimpleHeader"
            text="Bookings"
            to="/bookings"
            :is-active="activeIcon === 'bookings'"
          >
            <bookings-icon-filled v-if="activeIcon === 'bookings'" />
            <bookings-icon-outline v-else />
          </NavigationItem>
          <GlobalNavDropdown
            id="desktop-account-menu-btn"
            :name="account.name"
            :first-name="account.firstName"
            :last-name="account.lastName"
            :profile-image="avatarUrl"
            :account-type="accountType"
            :is-simple-header="isSimpleHeader"
            :trigger-width="80"
          />
          <Button
            v-if="!isSimpleHeader && basicAccount"
            value="Upgrade"
            variant="primary"
            href="/upgrade/pricing"
            size="medium"
            @click="upgradeClicked"
          />
        </nav>
      </header>
      <nav
        v-if="!isSimpleHeader && !hideMobileBottomNav"
        id="mobile-nav-bar"
        ref="mobileNavBar"
        class="flex h-full items-center icon-container-mobile-bottom"
        @keydown="onMobileBottomNavKeydown"
        @focusin="onMobileBottomNavFocusIn"
      >
        <NavigationItem
          text="Home"
          to="/account/seekers/dashboard"
          :is-active="activeIcon === 'home'"
          :is-mobile="true"
          :link-tab-index="mobileNavTabIndexFor(0)"
        >
          <home-icon-filled v-if="activeIcon === 'home'" />
          <home-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          text="Jobs"
          to="/seeker/jobs"
          :badge-num="receivedAppsCount"
          :is-active="activeIcon === 'jobs'"
          :is-mobile="true"
          :link-tab-index="mobileNavTabIndexFor(1)"
        >
          <jobs-icon-filled v-if="activeIcon === 'jobs'" />
          <jobs-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          text="Explore"
          to="/availability/search"
          :is-active="activeIcon === 'explore'"
          :is-mobile="true"
          :link-tab-index="mobileNavTabIndexFor(2)"
        >
          <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
          <magnifying-glass-icon-outline v-else />
        </NavigationItem>
        <NavigationItem
          v-if="!basicAccount"
          text="Bookings"
          to="/bookings"
          :is-active="activeIcon === 'bookings'"
          :is-mobile="true"
          :link-tab-index="mobileNavTabIndexFor(3)"
        >
          <bookings-icon-filled v-if="activeIcon === 'bookings'" />
          <bookings-icon-outline v-else />
        </NavigationItem>
        <GlobalNavDropdown
          id="mobile-account-menu-btn"
          :name="account.name"
          :first-name="account.firstName"
          :last-name="account.lastName"
          :profile-image="avatarUrl"
          :account-type="accountType"
          :is-simple-header="isSimpleHeader"
          overlay-position="bottom"
          size="small"
          :caret="false"
          trigger-layout="column"
          :trigger-width="72"
          overlay-align="left"
          :trigger-tab-index="mobileNavTabIndexFor(mobileNavAccountSlotIndex)"
        />
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
import startCase from 'lodash/startCase';
import { sendSegmentEvent } from '@/lib/segment';
import {
  bottomNavFocusableIndex,
  bottomNavRovingIndexFromFocusTarget,
  getMobileBottomNavFocusables,
  isMobileBottomNavKeyboardActive,
  nextBottomNavRovingIndex,
} from '@/components/nav/helpers/mobileBottomNavKeyboard';

import HomeIconFilled from '@/icons/global_nav/HomeIconFilled.vue';
import HomeIconOutline from '@/icons/global_nav/HomeIconOutline.vue';
import JobsIconFilled from '@/icons/global_nav/JobsIconFilled.vue';
import JobsIconOutline from '@/icons/global_nav/JobsIconOutline.vue';
import MagnifyingGlassIconFilled from '@/icons/global_nav/MagnifyingGlassIconFilled.vue';
import MagnifyingGlassIconOutline from '@/icons/global_nav/MagnifyingGlassIconOutline.vue';
import MessagesIconFilled from '@/icons/global_nav/MessagesIconFilled.vue';
import MessagesIconOutline from '@/icons/global_nav/MessagesIconOutline.vue';
import BookingsIconFilled from '@/icons/global_nav/BookingsIconFilled.vue';
import BookingsIconOutline from '@/icons/global_nav/BookingsIconOutline.vue';
import Badge from '@/components/badge/Badge.vue';
import Button from '../button/Button.vue';
import GlobalNavDropdown from './GlobalNavDropdown.vue';
import NavigationItem from '@/components/nav/components/NavigationItem.vue';
import NavigationLogo from '@/components/nav/components/NavigationLogo.vue';
import NavigationMobileTop from './NavigationMobileTop.vue';

export default {
  name: 'GlobalNav',
  components: {
    HomeIconFilled,
    HomeIconOutline,
    JobsIconFilled,
    JobsIconOutline,
    MagnifyingGlassIconOutline,
    MagnifyingGlassIconFilled,
    MessagesIconFilled,
    MessagesIconOutline,
    BookingsIconFilled,
    BookingsIconOutline,

    Badge,
    Button,
    GlobalNavDropdown,
    NavigationLogo,
    NavigationItem,
    NavigationMobileTop,
  },
  data() {
    return {
      mobileNavRovingIndex: 0,
    };
  },
  computed: {
    preventRedirectInRegistration(): boolean {
      return (
        this.$route.path.startsWith('/registration') ||
        this.$route.path.startsWith('/corporate-onboarding') ||
        this.$route.query.source === 'corporate-onboarding' ||
        this.$route.query.source === 'registration'
      );
    },
    unreadMessageCount(): number {
      return this.$store.getters['navigation/unreadMessageCount'];
    },
    avatarUrl(): string {
      return this.account?.croppedPhoto || this.account?.originalPhoto || '';
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    receivedAppsCount(): number {
      return this.$store.getters['navigation/receivedAppsCount'];
    },
    activeIcon() {
      const path = this.$route?.path ?? window.location.pathname;
      if (path === '/account/seekers/dashboard') return 'home';
      if (path.startsWith('/seeker/jobs')) return 'jobs';
      if (path.startsWith('/messages')) return 'messages';
      if (path === '/bookings' || path.startsWith('/bookings/')) return 'bookings';
      if (path === '/profile/provider/search' || path === '/availability/search') return 'explore';
      return '';
    },
    basicAccount(): boolean {
      return this.account?.isBasic();
    },
    accountType(): string {
      return startCase(this.account.accountType());
    },
    isSimpleHeader(): boolean {
      return this.$route.matched.some((r) => r.meta?.simpleHeader);
    },
    hideMobileBottomNav(): boolean {
      return this.$route.matched.some((r) => r.meta?.hideMobileBottomNav);
    },
    isMessagesRoute(): boolean {
      return this.$route.path.startsWith('/messages');
    },
    mobileNavAccountSlotIndex(): number {
      return this.basicAccount ? 3 : 4;
    },
    messagesMobileLinks() {
      const links: Array<{ href: string; label: string; badgeCount?: number; external?: boolean }> = [
        { href: '/account/seekers/dashboard', label: 'Home' },
        {
          href: '/seeker/my-jobs/opened',
          label: 'Jobs',
          badgeCount: this.receivedAppsCount,
        },
        { href: '/availability/search', label: 'Explore' },
      ];
      if (!this.basicAccount) {
        links.push({ href: '/bookings', label: 'Bookings' });
      }
      links.push({ href: '/account/settings', label: 'Account' });
      if (this.basicAccount) {
        links.push({ href: '/upgrade/pricing', label: 'Premium pricing' });
      }
      links.push(
        { href: 'https://www.sittercity.com/trust-and-safety', label: 'Trust & Safety', external: true },
        { href: 'https://support.sittercity.com/hc/en-us', label: 'Help', external: true },
        { href: '/logout', label: 'Log out' }
      );
      return links;
    },
  },
  watch: {
    mobileNavAccountSlotIndex() {
      this.$nextTick(() => {
        if (this.mobileNavRovingIndex > this.mobileNavAccountSlotIndex) {
          this.mobileNavRovingIndex = this.mobileNavAccountSlotIndex;
        }
      });
    },
  },
  mounted() {
    this.$store.dispatch('navigation/startPolling');
  },
  beforeUnmount() {
    this.$store.dispatch('navigation/stopPolling');
  },
  methods: {
    mobileNavTabIndexFor(slotIndex: number): number {
      return this.mobileNavRovingIndex === slotIndex ? 0 : -1;
    },
    getMobileNavFocusOrder(): HTMLElement[] {
      const nav = this.$refs.mobileNavBar as HTMLElement | undefined;
      return nav ? getMobileBottomNavFocusables(nav) : [];
    },
    onMobileBottomNavKeydown(event: KeyboardEvent) {
      if (!isMobileBottomNavKeyboardActive()) return;
      const nav = this.$refs.mobileNavBar as HTMLElement | undefined;
      const active = document.activeElement as HTMLElement | null;
      if (!nav || !active || !nav.contains(active)) return;
      if (active.closest('.account-menu__overlay')) return;
      const items = this.getMobileNavFocusOrder();
      if (items.length === 0) return;
      const idx = bottomNavFocusableIndex(active, items);
      if (idx < 0) return;
      const next = nextBottomNavRovingIndex(idx, event.key, items.length);
      if (next === null) return;
      event.preventDefault();
      this.mobileNavRovingIndex = next;
      this.$nextTick(() => {
        items[next]?.focus();
      });
    },
    onMobileBottomNavFocusIn(event: FocusEvent) {
      if (!isMobileBottomNavKeyboardActive()) return;
      const nav = this.$refs.mobileNavBar as HTMLElement | undefined;
      const target = event.target as HTMLElement | null;
      if (!nav || !target || !nav.contains(target)) return;
      const items = this.getMobileNavFocusOrder();
      const i = bottomNavRovingIndexFromFocusTarget(target, items);
      if (i !== null) this.mobileNavRovingIndex = i;
    },
    upgradeClicked() {
      sendSegmentEvent('GlobalNav_UpgradePricing', {});
    },
  },
};
</script>

<style lang="scss">
.global-navigation {
  z-index: 100;
  position: fixed;
  top: 0;
  width: 100%;
  background: var(--light-surface-foreground, #fff);
  display: flex;
  align-items: center;
  height: 48px;
  border-bottom: none;
  box-shadow: 0 2px 12px rgba(215, 216, 219, 0.9);

  @media only screen and (min-width: 768px) {
    height: 80px;
    border-bottom: 1px solid #d7d8d9;
    box-shadow: none;
  }

  .icon-container {
    gap: 12px;
    @media only screen and (min-width: 768px) {
      display: flex;
    }
    @media only screen and (min-width: 1024px) {
      gap: 32px;
    }
  }
}

.icon-container-mobile-bottom {
  z-index: 100;
  justify-content: space-around;
  height: 80px;
  width: 100vw;
  position: fixed;
  bottom: 0;
  background-color: var(--light-surface-foreground, #fff);
  @media only screen and (min-width: 768px) {
    visibility: hidden;
  }
  .nav-icon-group {
    width: 72px;
  }
}

.mobile-messaging-icon {
  @media only screen and (min-width: 768px) {
    display: none;
  }
  &:focus {
    outline: 2px dotted #bb0049;
  }
  &:hover {
    cursor: pointer;
    path {
      fill: #bb0049;
    }
  }
}

#ot-sdk-btn-floating.ot-floating-button {
  z-index: 51 !important;
}

@media (max-width: 767px) {
  body:has(#mobile-nav-bar) {
    #ot-sdk-btn-floating.ot-floating-button {
      bottom: 85px !important;
    }
    #launcher {
      margin-bottom: 85px !important;
    }
  }
}
</style>
